/**
 * Field definitions for every Utilities form. Field names match the
 * `values` / `lines` keys the backend expects (see the integration guide).
 * Labels, grouping and button text follow the Odoo form views in utilities_custom.
 *
 * type:   text | textarea | date | number | boolean | select | lookup | lookupMulti
 *         display | displayMany | money (read-only values computed by Odoo)
 * lookup: key in the /api/utilities/lookups response (lookups.<key>)
 *
 * readonly:   shown but never sent to the backend
 * postedOnly: hidden while the record is a draft
 * visibleIf / requiredIf: Odoo's invisible= / required= conditions
 * blankText:  what a blank value means, because the backend fills it in
 * groups:     the form's field groups, in order (two per row)
 * afterGroups: the single-column group under them
 * tabs:       notebook pages, either the lines list or one text field
 */

const DRAFT_POSTED = [
  { value: 'draft', label: 'Draft' },
  { value: 'posted', label: 'Posted' },
];

export const RESOURCES = {
  vendors: {
    key: 'vendors',
    label: 'Vendors',
    singular: 'Vendor',
    scope: 'any',
    listColumns: [
      { key: 'name', label: 'Company Name' },
      { key: 'date', label: 'Date', date: true },
      { key: 'phone', label: 'Phone' },
      { key: 'email', label: 'Email' },
      { key: 'branch', label: 'Branch' },
    ],
    emptyHelp: [
      'Create a new vendor registration',
      'Register new vendors/suppliers that will be used in utilities bills and payments.',
    ],
    formTitle: 'Vendor/Supplier Registration',
    postLabel: 'Register Vendor',
    states: [
      { value: 'draft', label: 'Draft' },
      { value: 'registered', label: 'Registered' },
    ],
    groups: [['date', 'name'], ['phone', 'email'], ['branch', 'bank_id'], ['bank_account_number']],
    afterGroups: [['partner_id', 'vendor_bank_account_id']],
    tabs: [{ key: 'notes', label: 'Notes', field: 'notes' }],
    fields: [
      { name: 'date', label: 'Date', type: 'date', required: true, defaultToday: true },
      { name: 'name', label: 'Company Name', type: 'text', required: true, placeholder: 'Company Name' },
      { name: 'phone', label: 'Phone', type: 'text', placeholder: 'Phone' },
      { name: 'email', label: 'Email', type: 'text', placeholder: 'Email' },
      { name: 'branch', label: 'Branch', type: 'text', placeholder: 'Branch' },
      { name: 'bank_id', label: 'Bank', type: 'lookup', lookup: 'banks', placeholder: 'Bank' },
      {
        name: 'bank_account_number',
        label: 'Bank Account Number',
        type: 'text',
        placeholder: 'Bank Account Number',
        visibleIf: (v) => Boolean(v.bank_id),
        requiredIf: (v) => Boolean(v.bank_id),
      },
      { name: 'partner_id', label: 'Odoo Vendor', type: 'display', readonly: true, postedOnly: true },
      { name: 'vendor_bank_account_id', label: 'Vendor Bank Account', type: 'display', readonly: true, postedOnly: true },
      { name: 'notes', label: 'Notes', type: 'textarea', placeholder: 'Additional notes...' },
    ],
    lines: null,
  },

  bills: {
    key: 'bills',
    label: 'Bills',
    singular: 'Bill',
    scope: 'any',
    listColumns: [
      { key: 'supplier_id', label: 'Select Supplier' },
      { key: 'bill_date', label: 'Bill Date', date: true },
      { key: 'previous_balance', label: 'Previous Balance', money: true },
      { key: 'total_amount', label: 'New Amount', money: true, sum: true },
    ],
    emptyHelp: [
      'Create a new utilities bill',
      'Create vendor bills for utilities expenses. These will create real accounting entries.',
    ],
    formTitle: 'Bills Form',
    postLabel: 'Post Bill',
    states: DRAFT_POSTED,
    groups: [
      ['supplier_id', 'bill_date'],
      ['name', 'accounting_date'],
      ['payment_mode', 'supplier_bank'],
      ['due_date', 'journal_id'],
    ],
    afterGroups: [['previous_balance', 'total_amount']],
    tabs: [
      { key: 'lines', label: 'Bill Lines', lines: true },
      { key: 'note', label: 'Note', field: 'note' },
    ],
    footerFields: ['vendor_bill_id'],
    fields: [
      { name: 'supplier_id', label: 'Select Supplier', type: 'lookup', lookup: 'suppliers', required: true },
      { name: 'bill_date', label: 'Bill Date', type: 'date', required: true, defaultToday: true },
      { name: 'name', label: 'Reference Number', type: 'display', readonly: true, defaultValue: 'New' },
      { name: 'accounting_date', label: 'Accounting Date', type: 'date', required: true, defaultToday: true },
      {
        name: 'payment_mode',
        label: 'Payment Mode',
        type: 'select',
        required: true,
        defaultValue: 'bank',
        options: [
          { value: 'bank', label: 'Bank' },
          { value: 'cash', label: 'Cash' },
        ],
      },
      { name: 'supplier_bank', label: 'Supplier Bank', type: 'text' },
      { name: 'due_date', label: 'Due Date', type: 'date' },
      { name: 'journal_id', label: 'Journal (Vendor Bills)', type: 'lookup', lookup: 'purchase_journals', required: true },
      { name: 'previous_balance', label: 'Previous Balance', type: 'money', readonly: true },
      { name: 'total_amount', label: 'Total Amount', type: 'money', readonly: true },
      { name: 'note', label: 'Note', type: 'textarea', placeholder: 'Note' },
      { name: 'vendor_bill_id', label: 'Vendor Bill', type: 'display', readonly: true, postedOnly: true },
    ],
    lines: {
      label: 'Bill Lines',
      columns: [
        { name: 'description', label: 'Description', type: 'text', required: true, width: '25%', placeholder: 'Description (Electricity Bill Dr Addow Campus)' },
        { name: 'account_id', label: 'Account (Expenses)', type: 'lookup', lookup: 'expense_accounts', required: true, width: '24%', placeholder: 'Account (Expenses (Chart of Accounts))' },
        { name: 'qty', label: 'Qty', type: 'number', defaultValue: 1, width: '12%', placeholder: 'Qty' },
        { name: 'amount', label: 'Amount', type: 'number', required: true, width: '12%', placeholder: 'Amount' },
        { name: 'tax_ids', label: 'Taxes', type: 'lookupMulti', lookup: 'purchase_taxes', placeholder: 'Taxes' },
      ],
    },
  },

  'single-payments': {
    key: 'single-payments',
    label: 'Single Payments',
    singular: 'Single Payment',
    scope: 'any',
    listColumns: [
      { key: 'name', label: 'Reference' },
      { key: 'vendor_id', label: 'Select Vendor' },
      { key: 'payment_date', label: 'Payment Date', date: true },
      { key: 'amount', label: 'Amount', money: true, sum: true },
    ],
    emptyHelp: [
      'Create a new single payment',
      'Make a single payment to a vendor. This will create real accounting entries.',
    ],
    formTitle: 'Single Payment Form',
    postLabel: 'Post Payment',
    states: DRAFT_POSTED,
    groups: [
      ['internal_transfer', 'payment_type'],
      ['vendor_id', 'amount'],
      ['previous_balance', 'payment_date'],
      ['bank_journal_id', 'cheque_number'],
      ['vendor_bank_account_id'],
    ],
    afterGroups: [['memo']],
    footerFields: ['account_payment_id'],
    fields: [
      { name: 'internal_transfer', label: 'Internal Transfer', type: 'boolean' },
      {
        name: 'payment_type',
        label: 'Payment Type',
        type: 'select',
        required: true,
        defaultValue: 'send',
        options: [
          { value: 'send', label: 'Send' },
          { value: 'receive', label: 'Receive' },
        ],
      },
      { name: 'vendor_id', label: 'Select Vendor', type: 'lookup', lookup: 'suppliers', required: true },
      { name: 'amount', label: 'Amount', type: 'number', required: true },
      { name: 'previous_balance', label: 'Previous Balance', type: 'money', readonly: true },
      { name: 'payment_date', label: 'Payment Date', type: 'date', required: true, defaultToday: true },
      { name: 'bank_journal_id', label: 'Select Bank (Journal)', type: 'lookup', lookup: 'bank_journals', required: true },
      { name: 'cheque_number', label: 'Cheque Number', type: 'text' },
      { name: 'vendor_bank_account_id', label: 'Vendor Bank Account', type: 'display', readonly: true },
      { name: 'memo', label: 'Memo/Note', type: 'textarea', noLabel: true, placeholder: 'Memo/Note ()' },
      { name: 'account_payment_id', label: 'Account Payment', type: 'display', readonly: true, postedOnly: true },
    ],
    lines: null,
  },

  'batch-payments': {
    key: 'batch-payments',
    label: 'Batch Payments',
    singular: 'Batch Payment',
    scope: 'any',
    listColumns: [
      { key: 'bank_journal_id', label: 'Bank' },
      { key: 'cheque_number', label: 'Cheque Number' },
      { key: 'batch_type_label', label: 'Batch Type' },
      { key: 'date', label: 'Date', date: true },
      { key: 'total_amount', label: 'Total Amount', money: true, sum: true },
    ],
    emptyHelp: [
      'Create a new batch payment',
      'Pay multiple bills at once. This will create real accounting entries.',
    ],
    formTitle: 'Batch Payment Form',
    postLabel: 'Post Batch Payment',
    states: DRAFT_POSTED,
    groups: [
      ['batch_type', 'date'],
      ['bank_journal_id', 'cheque_number'],
      ['name', 'payment_method_id'],
    ],
    afterGroups: [['total_amount']],
    tabs: [{ key: 'lines', label: 'Batch Content (Select Multiple Bills to pay at once)', lines: true }],
    footerFields: ['account_payment_ids'],
    fields: [
      {
        name: 'batch_type',
        label: 'Batch Type',
        type: 'select',
        required: true,
        defaultValue: 'outbound',
        options: [
          { value: 'outbound', label: 'Outbound' },
          { value: 'inbound', label: 'Inbound' },
        ],
      },
      { name: 'date', label: 'Date', type: 'date', required: true, defaultToday: true },
      { name: 'bank_journal_id', label: 'Bank', type: 'lookup', lookup: 'bank_journals', required: true },
      { name: 'cheque_number', label: 'Cheque Number', type: 'text' },
      { name: 'name', label: 'Payment Reference', type: 'display', readonly: true, defaultValue: 'New' },
      { name: 'payment_method_id', label: 'Payment Method', type: 'lookup', lookup: 'payment_methods' },
      { name: 'total_amount', label: 'Total Amount', type: 'money', readonly: true },
      { name: 'account_payment_ids', label: 'Account Payments', type: 'displayMany', readonly: true, postedOnly: true },
    ],
    lines: {
      label: 'Batch Content',
      columns: [
        { name: 'bill_id', label: 'Bill', type: 'outstandingBill', required: true, width: '24%', placeholder: 'Select Bill (search by description)' },
        { name: 'bill_name', label: 'Bill Name', type: 'display', readonly: true },
        { name: 'bill_description', label: 'Description', type: 'display', readonly: true },
        { name: 'bill_date', label: 'Date', type: 'date', readonly: true, width: '11%' },
        { name: 'vendor_id', label: 'Vendor', type: 'display', readonly: true },
        { name: 'amount', label: 'Amount', type: 'number', width: '11%', placeholder: 'Amount' },
      ],
    },
  },

  requests: {
    key: 'requests',
    label: 'Requests',
    singular: 'Request',
    scope: 'child',
    listColumns: [
      { key: 'name', label: 'Serial Number' },
      { key: 'reference', label: 'Reference' },
      { key: 'customer_id', label: 'Select Customer' },
      { key: 'date', label: 'Date', date: true },
      { key: 'total_amount', label: 'Total Amount', money: true, sum: true },
    ],
    emptyHelp: [
      'Create a new request form',
      'Create customer invoices/requests. These will create real accounting entries.',
      'Note: This form is not available in SHAG GROUP OF COMPANIES. Please switch to a child company.',
    ],
    formTitle: 'Request Form',
    postLabel: 'Post Request',
    states: DRAFT_POSTED,
    groups: [
      ['name', 'customer_id'],
      ['date', 'due_date'],
      ['journal_id', 'reference'],
    ],
    afterGroups: [['total_amount']],
    tabs: [{ key: 'lines', label: 'Request Lines', lines: true }],
    footerFields: ['customer_invoice_id'],
    fields: [
      { name: 'name', label: 'Invoice', type: 'display', readonly: true, defaultValue: 'New' },
      // Left blank, the backend sets the parent company's own partner. Don't prefill by
      // name: other customers can share that name, and their invoices can't be deposited.
      { name: 'customer_id', label: 'Requesting From (Parent Company)', type: 'lookup', lookup: 'customers', blankText: 'SHAG GROUP OF COMPANIES', help: 'This will automatically be set to SHAG GROUP OF COMPANIES' },
      { name: 'date', label: 'Date', type: 'date', required: true, defaultToday: true },
      { name: 'due_date', label: 'Due Date', type: 'date', required: true },
      { name: 'journal_id', label: 'Journal (Customer Invoice)', type: 'lookup', lookup: 'sale_journals', required: true },
      { name: 'reference', label: 'Reference', type: 'text' },
      { name: 'total_amount', label: 'Total Amount', type: 'money', readonly: true },
      { name: 'customer_invoice_id', label: 'Customer Invoice', type: 'display', readonly: true, postedOnly: true },
    ],
    lines: {
      label: 'Request Lines',
      columns: [
        { name: 'description', label: 'Description', type: 'text', required: true, width: '25%', placeholder: 'Description (Budget January)' },
        { name: 'account_id', label: 'Account', type: 'lookup', lookup: 'income_accounts', required: true, width: '24%', placeholder: 'Account: Budget' },
        { name: 'qty', label: 'QTY', type: 'number', defaultValue: 1, width: '12%', placeholder: 'QTY' },
        { name: 'amount', label: 'Amount', type: 'number', required: true, width: '12%', placeholder: 'Amount' },
        { name: 'tax_ids', label: 'Taxes', type: 'lookupMulti', lookup: 'sale_taxes' },
      ],
    },
  },

  deposits: {
    key: 'deposits',
    label: 'Single Deposited Form',
    singular: 'Deposit',
    scope: 'parent',
    listColumns: [
      { key: 'invoice_id', label: 'Select Invoice' },
      { key: 'child_company_id', label: 'Child Company' },
      { key: 'child_bank_journal_id', label: 'Child Company Receiving Bank' },
      { key: 'bank_ref_number', label: 'Bank Ref Number' },
      { key: 'bank_journal_id', label: 'Parent Company Bank' },
      { key: 'deposited_date', label: 'Deposited Date', date: true },
      { key: 'amount', label: 'Amount', money: true, sum: true },
    ],
    emptyHelp: [
      'Create a new single deposit',
      'Record customer payment deposits. This will create real accounting entries.',
      'Note: This form is only available in SHAG GROUP OF COMPANIES (parent company).',
    ],
    formTitle: 'Single Deposited Form',
    postLabel: 'Post Deposit',
    states: DRAFT_POSTED,
    groups: [
      ['internal_transfer'],
      ['invoice_id', 'customer_id', 'child_bank_journal_id'],
      ['amount', 'deposited_date'],
      ['bank_journal_id', 'bank_ref_number'],
    ],
    afterGroups: [['memo']],
    footerFields: ['account_payment_id'],
    fields: [
      { name: 'internal_transfer', label: 'Internal Transfer', type: 'boolean' },
      { name: 'invoice_id', label: 'Select Invoice', type: 'depositInvoice', required: true, placeholder: 'Search invoices from child companies requesting money from parent', help: 'Select an invoice from a child company requesting money from SHAG GROUP OF COMPANIES.' },
      { name: 'customer_id', label: 'Child Company (Receiving Deposit)', type: 'display', readonly: true, help: 'The child company receiving the deposit (company that issued the invoice).' },
      {
        name: 'child_bank_journal_id',
        label: 'Child Company Receiving Bank',
        type: 'childBank',
        help: 'Select the bank account in the child company where you want to deposit the money',
        visibleIf: (v) => Boolean(v.invoice_id),
        requiredIf: (v) => Boolean(v.invoice_id),
      },
      { name: 'amount', label: 'Amount', type: 'number', required: true },
      { name: 'deposited_date', label: 'Deposited Date', type: 'date', required: true, defaultToday: true },
      { name: 'bank_journal_id', label: 'Select Bank (Journal)', type: 'lookup', lookup: 'bank_journals', required: true, help: 'Select a bank from SHAG GROUP OF COMPANIES (parent company) to debit the payment from' },
      { name: 'bank_ref_number', label: 'Bank Ref Number', type: 'text' },
      { name: 'memo', label: 'Memo/Note', type: 'textarea', noLabel: true, placeholder: 'Memo/Note ( Budget Jan - Recieved)' },
      { name: 'account_payment_id', label: 'Account Payment', type: 'display', readonly: true, postedOnly: true },
    ],
    lines: null,
  },
};

export const RESOURCE_LIST = Object.values(RESOURCES);

/** Forms a company can open: requests are child-only, deposits are parent-only. */
export function resourceAllowedForCompany(resource, company) {
  if (!resource || !company) return false;
  if (resource.scope === 'child') return !company.is_parent;
  if (resource.scope === 'parent') return Boolean(company.is_parent);
  return true;
}

/** Lookup keys the form needs, so the API only fetches what the page uses. */
export function lookupTypesFor(resource) {
  const types = new Set();
  resource.fields.forEach((f) => f.lookup && types.add(f.lookup));
  resource.lines?.columns.forEach((c) => c.lookup && types.add(c.lookup));
  return [...types];
}

function todayIso() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Starting values for a new form, the same defaults the Odoo form opens with. */
export function emptyValuesFor(resource) {
  const values = {};
  resource.fields.forEach((f) => {
    if (f.defaultToday) values[f.name] = todayIso();
    else if (f.defaultValue !== undefined) values[f.name] = f.defaultValue;
    else if (f.type === 'boolean') values[f.name] = false;
    else if (f.type === 'money') values[f.name] = 0;
    else values[f.name] = '';
  });
  return values;
}

export function emptyLineFor(resource) {
  const line = {};
  resource.lines?.columns.forEach((c) => {
    line[c.name] = c.defaultValue ?? (c.type === 'lookupMulti' ? [] : '');
  });
  return line;
}
